/**
 * お気に入り機能 - JavaScript
 * Shopify テーマ用
 */

class FavoritesManager {
  constructor() {
    this.apiBase = '/apps/favorites';
    this.favorites = [];
    this.isLoggedIn = this.checkLoginStatus();

    if (this.isLoggedIn) {
      this.init();
    } else {
      this.attachEventListeners();
    }
  }

  /**
   * ログイン状態の確認
   */
  checkLoginStatus() {
    // customerオブジェクトの存在確認
    return typeof window.customer !== 'undefined' && window.customer !== null;
  }

  /**
   * 初期化
   * マイページ同様: 先にローカルストレージで即時表示し、メタフィールド or API で差分があれば上書き
   */
  async init() {
    try {
      // 1. 先にローカルストレージの値で即時表示（速い）
      const cached = this.getCachedFavoritesList();
      if (cached.valid) {
        this.favorites = cached.list;
      }
      this.attachEventListeners();
      this.updateAllButtonStates();

      // 2. メタフィールド or index.php で正しい一覧を取得し、差分あれば上書き
      let list = null;
      const fromMetafield = this.getFavoritesListFromMetafield();
      if (fromMetafield !== null) {
        list = fromMetafield;
      } else {
        try {
          const data = await this.loadFavorites();
          list = (data.favorites || []).filter(f => f && f.product_handle);
        } catch (e) {
          return;
        }
      }
      if (list && this._listDiff(this.favorites, list)) {
        this.favorites = list;
        this.saveFavoritesToStorage();
        this.updateAllButtonStates();
      }
    } catch (error) {
      console.error('Favorites initialization error:', error);
    }
  }

  /**
   * お気に入りデータの読み込み
   */
  async loadFavorites() {
    try {
      const response = await fetch(`${this.apiBase}/index.php`);
      if (!response.ok) {
        throw new Error('Failed to load favorites');
      }
      
      const data = await response.json();
      this.favorites = data.favorites || [];
      
      // ローカルストレージにキャッシュ
      localStorage.setItem('favorites_cache', JSON.stringify({
        data: this.favorites,
        timestamp: Date.now()
      }));
      
      return data;
    } catch (error) {
      console.error('Load favorites error:', error);
      // キャッシュから読み込み
      this.loadFromCache();
      throw error;
    }
  }

  /**
   * キャッシュから読み込み
   */
  loadFromCache() {
    try {
      const cache = localStorage.getItem('favorites_cache');
      if (cache) {
        const parsed = JSON.parse(cache);
        // 24時間以内のキャッシュのみ使用
        if (Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
          this.favorites = parsed.data || [];
        }
      }
    } catch (error) {
      console.error('Load from cache error:', error);
    }
  }

  /**
   * ローカルストレージからお気に入り一覧を取得（表示用・API を待たない）
   * @returns {{ list: Array, valid: boolean }}
   */
  getCachedFavoritesList() {
    try {
      const raw = localStorage.getItem('favorites_cache');
      if (!raw) return { list: [], valid: false };
      const parsed = JSON.parse(raw);
      const list = (parsed.data || []).filter(f => f && f.product_handle);
      return { list, valid: true };
    } catch (e) {
      return { list: [], valid: false };
    }
  }

  /**
   * ローカルストレージに保存（追加・削除後に呼ぶ）
   */
  saveFavoritesToStorage() {
    try {
      localStorage.setItem('favorites_cache', JSON.stringify({
        data: this.favorites,
        timestamp: Date.now()
      }));
    } catch (e) {}
  }

  /**
   * 表示中の一覧とサーバー一覧が異なるか（他端末で追加・削除されたか）
   */
  _listDiff(a, b) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return true;
    const idsA = a.map(x => x.product_id).sort().join(',');
    const idsB = b.map(x => x.product_id).sort().join(',');
    return idsA !== idsB;
  }

  /**
   * カスタムフィールド（Liquid で出力したメタフィールド）から一覧を取得
   * index.php 不要・最速。メタフィールドの「ストアフロントアクセス」を有効にすること。
   * @returns {Array|null} 使える一覧、または null
   */
  getFavoritesListFromMetafield() {
    try {
      if (typeof window.__favoritesFromMetafield === 'undefined') return null;
      const raw = window.__favoritesFromMetafield;
      const list = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!Array.isArray(list)) return null;
      return list.filter(f => f && f.product_handle);
    } catch (e) {
      return null;
    }
  }

  /**
   * お気に入りに追加
   * @param {string} productId - 商品ID
   * @param {string} productHandle - 商品ハンドル
   * @param {Object} [itemInfo] - GA4用 { productName, productPrice }
   */
  async addFavorite(productId, productHandle, itemInfo = {}) {
    try {
      const response = await fetch(`${this.apiBase}/add.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_id: productId,
          product_handle: productHandle
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add favorite');
      }

      const data = await response.json();

      if (data.success) {
        if (!data.already_exists) {
          this.favorites.push({
            product_id: productId,
            product_handle: productHandle,
            added_at: new Date().toISOString()
          });
          this.saveFavoritesToStorage();
        }
        this.updateButtonState(productId, true);
        this.showNotification(data.already_exists ? (this.getStrings().alreadyInFavorites || data.message) : this.getStrings().addedToFavorites);
        this.trackFavoritesEvent('add_to_wishlist', productId, productHandle, itemInfo);
      }

      return data;
    } catch (error) {
      console.error('Add favorite error:', error);
      this.showNotification(this.getStrings().favoritesError, 'error');
      throw error;
    }
  }

  /**
   * お気に入りから削除
   * @param {string} productId - 商品ID
   * @param {Object} [itemInfo] - GA4用 { productName, productPrice }
   */
  async removeFavorite(productId, itemInfo = {}) {
    try {
      const response = await fetch(`${this.apiBase}/remove.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ product_id: productId })
      });

      if (!response.ok) {
        throw new Error('Failed to remove favorite');
      }

      const data = await response.json();

      if (data.success) {
        const handle = this.favorites.find(f => f.product_id == productId)?.product_handle || '';
        this.favorites = this.favorites.filter(item => item.product_id != productId);
        this.saveFavoritesToStorage();
        this.updateButtonState(productId, false);
        this.showNotification(this.getStrings().removedFromFavorites);
        this.trackFavoritesEvent('remove_from_wishlist', productId, handle, itemInfo);
      }

      return data;
    } catch (error) {
      console.error('Remove favorite error:', error);
      this.showNotification(this.getStrings().favoritesError, 'error');
      throw error;
    }
  }

  /**
   * お気に入りトグル
   * @param {string} productId - 商品ID
   * @param {string} productHandle - 商品ハンドル
   * @param {Object} [itemInfo] - GA4用 { productName, productPrice }（ボタンの data 属性から渡す）
   */
  async toggleFavorite(productId, productHandle, itemInfo = {}) {
    if (!this.isLoggedIn) {
      this.showLoginRequiredNotification();
      return;
    }
    const wasFavorited = this.isFavorited(productId);
    this.setFavoriteButtonsLoading(productId, true, wasFavorited ? 'remove' : 'add');
    try {
      if (wasFavorited) {
        await this.removeFavorite(productId, itemInfo);
      } else {
        await this.addFavorite(productId, productHandle, itemInfo);
      }
    } catch (e) {
      this.updateButtonState(productId, wasFavorited);
    } finally {
      this.setFavoriteButtonsLoading(productId, false);
    }
  }

  /**
   * 追加・削除 API 待ちの見た目（スピナー・文言）
   * @param {'add'|'remove'} mode
   */
  setFavoriteButtonsLoading(productId, loading, mode = 'add') {
    const pidEsc =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape(String(productId))
        : String(productId).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const buttons = document.querySelectorAll(
      `[data-product-id="${pidEsc}"]:not(.favorites-remove-btn)`
    );
    const strings = this.getStrings();
    buttons.forEach(button => {
      const icon = button.querySelector('.favorites-btn__icon');
      const text = button.querySelector('.favorites-btn__text');
      if (loading) {
        button.classList.add('is-favorites-loading');
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
        if (icon) {
          icon.classList.remove('fa-regular', 'fa-solid', 'fa-heart');
          icon.classList.add('fa-solid', 'fa-spinner', 'fa-spin');
          icon.setAttribute('aria-hidden', 'true');
        }
        const loadingLabel = mode === 'remove'
          ? (strings.removingFromFavorites || '解除中…')
          : (strings.addingToFavorites || '追加中…');
        if (text) text.textContent = loadingLabel;
        button.setAttribute('aria-label', loadingLabel);
      } else {
        button.classList.remove('is-favorites-loading');
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }
    });
  }

  /**
   * 商品がお気に入りかチェック
   */
  isFavorited(productId) {
    return this.favorites.some(item => item.product_id == productId);
  }

  /**
   * GA4 / dataLayer でお気に入りイベントを送信（商品ID・商品名のみ）
   * - add_to_wishlist: GA4推奨イベント（ウイッシュリストに追加）
   * - remove_from_wishlist: カスタムイベント（ウイッシュリストから削除）
   * ecommerce 構造は GA4/GTM で必要なため items 配列で送信。
   * @param {string} action - 'add_to_wishlist' | 'remove_from_wishlist'
   * @param {string} productId - 商品ID
   * @param {string} productHandle - 商品ハンドル
   * @param {Object} [itemInfo] - { productName }
   */
  trackFavoritesEvent(action, productId, productHandle, itemInfo = {}) {
    const itemName = itemInfo.productName || productHandle || String(productId);
    const items = [{
      item_id: String(productId),
      item_name: itemName
    }];

    // トップレベルの item_id / item_name も送る（GA4 のカスタムディメンションが items 配列を展開しない場合に対応）
    const eventParams = {
      item_id: String(productId),
      item_name: itemName,
      ecommerce: { items: items }
    };

    // dataLayer（GTM / gtag 両対応）。ecommerce.items は GA4 で必須の構造
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: action,
      ...eventParams
    });

    // gtag が読み込まれている場合（theme.liquid の GA4 を有効にすると送信される）
    if (typeof gtag === 'function') {
      gtag('event', action, { items: items, item_id: eventParams.item_id, item_name: eventParams.item_name });
    }
  }

  /**
   * ボタンの状態を更新（追加用ボタンのみ。削除ボタン✗は対象外）
   */
  updateButtonState(productId, isFavorited) {
    const buttons = document.querySelectorAll(`[data-product-id="${productId}"]:not(.favorites-remove-btn)`);
    
    buttons.forEach(button => {
      const icon = button.querySelector('.favorites-btn__icon');
      const text = button.querySelector('.favorites-btn__text');
      
      const strings = this.getStrings();
      if (icon) {
        icon.classList.remove('fa-spinner', 'fa-spin');
      }
      if (isFavorited) {
        button.classList.add('is-favorited');
        if (icon) {
          icon.classList.remove('fa-regular');
          icon.classList.add('fa-solid', 'fa-heart');
        }
        if (text) text.textContent = strings.favorited;
        button.setAttribute('aria-label', strings.removeFromFavorites);
      } else {
        button.classList.remove('is-favorited');
        if (icon) {
          icon.classList.remove('fa-solid');
          icon.classList.add('fa-regular', 'fa-heart');
        }
        if (text) text.textContent = strings.addToFavorites;
        button.setAttribute('aria-label', strings.addToFavorites);
      }
    });
  }

  /**
   * すべてのボタンの状態を更新（追加用♡のみ。削除ボタン✗は対象外）
   */
  updateAllButtonStates() {
    const buttons = document.querySelectorAll('.favorites-btn:not(.favorites-remove-btn)');
    
    buttons.forEach(button => {
      const productId = button.getAttribute('data-product-id');
      if (productId) {
        const isFavorited = this.isFavorited(productId);
        this.updateButtonState(productId, isFavorited);
      }
    });
  }

  /**
   * イベントリスナーを設定
   */
  attachEventListeners() {
    // ボタンクリックイベント（追加用♡のみ。削除ボタン✗は各カードで個別に処理）
    document.addEventListener('click', (e) => {
      const button = e.target.closest('.favorites-btn:not(.favorites-remove-btn)');
      if (button) {
        e.preventDefault();
        const productId = button.getAttribute('data-product-id');
        const productHandle = button.getAttribute('data-product-handle');
        if (productId && productHandle) {
          const itemInfo = {
            productName: button.getAttribute('data-product-title') || '',
            productPrice: button.getAttribute('data-product-price') || ''
          };
          this.toggleFavorite(productId, productHandle, itemInfo);
        }
      }
    });
  }

  /**
   * 通知を表示
   */
  showNotification(message, type = 'success') {
    // 既存の通知を削除
    const existing = document.querySelector('.favorites-notification');
    if (existing) {
      existing.remove();
    }

    // 通知要素を作成
    const notification = document.createElement('div');
    notification.className = `favorites-notification favorites-notification--${type}`;
    notification.textContent = message;
    document.body.appendChild(notification);

    // アニメーション
    setTimeout(() => notification.classList.add('is-visible'), 10);

    // 3秒後に非表示
    setTimeout(() => {
      notification.classList.remove('is-visible');
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  /**
   * 最近見た商品と同様に /products/{handle}.js で1件取得
   */
  async fetchProductByHandle(handle) {
    const res = await fetch('/products/' + encodeURIComponent(handle) + '.js');
    if (!res.ok) return null;
    const p = await res.json();
    const img = p.featured_image || (p.images && p.images[0]);
    const variant = (p.variants && p.variants[0]) || {};
    return {
      id: 'gid://shopify/Product/' + p.id,
      title: p.title || '',
      handle: p.handle || handle,
      featuredImage: img ? { url: img.src || img } : null,
      priceRange: {
        minVariantPrice: { amount: String(variant.price || 0), currencyCode: 'JPY' }
      }
    };
  }

  /**
   * お気に入り一覧ページの読み込み
   * 表示: 1) メタフィールド → 2) ローカルストレージ → 3) index.php。追加・削除は非同期でサーバーに同期しつつローカルも更新。
   */
  async loadFavoritesPage() {
    const loadingEl = document.getElementById('favorites-loading');
    const sectionEl = document.getElementById('favorites-section');
    const gridEl = document.getElementById('favorites-grid');
    const emptyEl = document.getElementById('favorites-empty');
    const errorEl = document.getElementById('favorites-error');

    let list = this.getFavoritesListFromMetafield();
    if (list !== null) {
      if (loadingEl) loadingEl.style.display = 'none';
      if (list.length === 0) {
        if (emptyEl) emptyEl.style.display = 'block';
        this.loadFavorites().then(d => { if (d && d.favorites && d.favorites.length > 0) { this.favorites = d.favorites; this.saveFavoritesToStorage(); this._renderFavoritesPageWithList((d.favorites || []).filter(f => f.product_handle)); } }).catch(() => {});
        return;
      }
      if (sectionEl) sectionEl.style.display = 'block';
      if (gridEl) gridEl.style.display = '';
      gridEl.innerHTML = '';
      this.loadFavorites().then(data => {
        if (!data || !data.favorites) return;
        const serverList = (data.favorites || []).filter(f => f.product_handle);
        if (this._listDiff(list, serverList)) {
          this.favorites = data.favorites;
          this.saveFavoritesToStorage();
          return this._renderFavoritesPageWithList(serverList);
        }
      }).catch(() => {});
    } else {
      const cached = this.getCachedFavoritesList();
      if (cached.valid) {
        list = cached.list;
        if (loadingEl) loadingEl.style.display = 'none';
        if (list.length === 0) {
          if (emptyEl) emptyEl.style.display = 'block';
          this.loadFavorites().catch(() => {});
          return;
        }
        if (sectionEl) sectionEl.style.display = 'block';
        if (gridEl) gridEl.style.display = '';
        gridEl.innerHTML = '';
        this.loadFavorites()
          .then(data => {
            if (!data || !data.favorites) return;
            const serverList = (data.favorites || []).filter(f => f.product_handle);
            if (this._listDiff(list, serverList)) {
              this.favorites = data.favorites;
              this.saveFavoritesToStorage();
              return this._renderFavoritesPageWithList(serverList);
            }
          })
          .catch(() => {});
      } else {
        try {
          const data = await this.loadFavorites();
          list = (data.favorites || []).filter(f => f.product_handle);
        } catch (error) {
          if (loadingEl) loadingEl.style.display = 'none';
          if (errorEl) errorEl.style.display = 'block';
          return;
        }
        if (loadingEl) loadingEl.style.display = 'none';
        if (!list || list.length === 0) {
          if (emptyEl) emptyEl.style.display = 'block';
          return;
        }
        if (sectionEl) sectionEl.style.display = 'block';
        if (gridEl) gridEl.style.display = '';
        gridEl.innerHTML = '';
      }
    }

    const products = await Promise.all(list.map(f => this.fetchProductByHandle(f.product_handle)));
    const valid = products.filter(Boolean);
    if (valid.length > 0) this.renderFavoritesGrid(valid, gridEl);
    if (gridEl && gridEl.children.length === 0 && emptyEl) emptyEl.style.display = 'block';
  }

  /**
   * マイページトップのお気に入りセクション読み込み
   * 表示: 1) メタフィールド → 2) ローカルストレージ → 3) index.php。追加・削除は非同期でサーバーに同期しつつローカルも更新。
   */
  async loadMypageFavorites() {
    const loadingEl = document.getElementById('mypage-favorites-loading');
    const gridEl = document.getElementById('mypage-favorites-grid');
    const emptyEl = document.getElementById('mypage-favorites-empty');
    const viewAllEl = document.getElementById('mypage-favorites-view-all');

    if (!gridEl) return;

    let list = this.getFavoritesListFromMetafield();
    if (list !== null) {
      if (loadingEl) loadingEl.style.display = 'none';
      if (list.length === 0) {
        if (emptyEl) emptyEl.style.display = 'block';
        if (gridEl) gridEl.style.display = 'none';
        this.loadFavorites().then(d => { if (d && d.favorites && d.favorites.length > 0) { this.favorites = d.favorites; this.saveFavoritesToStorage(); this._renderMypageFavoritesWithList((d.favorites || []).filter(f => f.product_handle)); } }).catch(() => {});
        return;
      }
      if (gridEl) gridEl.style.display = '';
      gridEl.innerHTML = '';
      if (emptyEl) emptyEl.style.display = 'none';
      this.loadFavorites().then(data => {
        if (!data || !data.favorites) return;
        const serverList = (data.favorites || []).filter(f => f.product_handle);
        if (this._listDiff(list, serverList)) {
          this.favorites = data.favorites;
          this.saveFavoritesToStorage();
          return this._renderMypageFavoritesWithList(serverList);
        }
      }).catch(() => {});
    } else {
      const cached = this.getCachedFavoritesList();
      if (cached.valid) {
        list = cached.list;
        if (loadingEl) loadingEl.style.display = 'none';
        if (list.length === 0) {
          if (emptyEl) emptyEl.style.display = 'block';
          if (gridEl) gridEl.style.display = 'none';
          this.loadFavorites().catch(() => {});
          return;
        }
        if (gridEl) gridEl.style.display = '';
        gridEl.innerHTML = '';
        if (emptyEl) emptyEl.style.display = 'none';
        this.loadFavorites()
          .then(data => {
            if (!data || !data.favorites) return;
            const serverList = (data.favorites || []).filter(f => f.product_handle);
            if (this._listDiff(list, serverList)) {
              this.favorites = data.favorites;
              this.saveFavoritesToStorage();
              return this._renderMypageFavoritesWithList(serverList);
            }
          })
          .catch(() => {});
      } else {
        try {
          if (loadingEl) loadingEl.style.display = 'block';
          if (emptyEl) emptyEl.style.display = 'none';
          const data = await this.loadFavorites();
          list = (data.favorites || []).filter(f => f.product_handle);
        } catch (error) {
          if (loadingEl) loadingEl.style.display = 'none';
          if (emptyEl) emptyEl.style.display = 'block';
          return;
        }
        if (loadingEl) loadingEl.style.display = 'none';
        if (!list || list.length === 0) {
          if (emptyEl) emptyEl.style.display = 'block';
          if (gridEl) gridEl.style.display = 'none';
          return;
        }
        if (gridEl) gridEl.style.display = '';
        gridEl.innerHTML = '';
        if (emptyEl) emptyEl.style.display = 'none';
      }
    }

    const toFetch = list.slice(0, 4);
    const products = await Promise.all(toFetch.map(f => this.fetchProductByHandle(f.product_handle)));
    const valid = products.filter(Boolean);
    if (valid.length > 0) this.renderMypageFavoritesGrid(valid, gridEl);
    if (list.length > 4 && viewAllEl) viewAllEl.style.display = 'block';
  }

  /**
   * サーバー一覧でマイページグリッドを再描画（他端末で変更があったときに同期）
   */
  async _renderMypageFavoritesWithList(serverList) {
    const gridEl = document.getElementById('mypage-favorites-grid');
    const emptyEl = document.getElementById('mypage-favorites-empty');
    const viewAllEl = document.getElementById('mypage-favorites-view-all');
    if (!gridEl) return;
    if (!serverList || serverList.length === 0) {
      if (emptyEl) emptyEl.style.display = 'block';
      gridEl.style.display = 'none';
      gridEl.innerHTML = '';
      if (viewAllEl) viewAllEl.style.display = 'none';
      return;
    }
    if (emptyEl) emptyEl.style.display = 'none';
    gridEl.style.display = '';
    gridEl.innerHTML = '';
    const toFetch = serverList.slice(0, 4);
    const products = await Promise.all(toFetch.map(f => this.fetchProductByHandle(f.product_handle)));
    const valid = products.filter(Boolean);
    if (valid.length > 0) this.renderMypageFavoritesGrid(valid, gridEl);
    if (serverList.length > 4 && viewAllEl) viewAllEl.style.display = 'block';
  }

  /**
   * サーバー一覧でお気に入りページグリッドを再描画（他端末で変更があったときに同期）
   */
  async _renderFavoritesPageWithList(serverList) {
    const sectionEl = document.getElementById('favorites-section');
    const gridEl = document.getElementById('favorites-grid');
    const emptyEl = document.getElementById('favorites-empty');
    if (!gridEl) return;
    if (!serverList || serverList.length === 0) {
      if (sectionEl) sectionEl.style.display = 'none';
      if (emptyEl) emptyEl.style.display = 'block';
      gridEl.innerHTML = '';
      return;
    }
    if (sectionEl) sectionEl.style.display = 'block';
    if (emptyEl) emptyEl.style.display = 'none';
    gridEl.style.display = '';
    gridEl.innerHTML = '';
    const products = await Promise.all(serverList.map(f => this.fetchProductByHandle(f.product_handle)));
    const valid = products.filter(Boolean);
    if (valid.length > 0) this.renderFavoritesGrid(valid, gridEl);
    if (gridEl.children.length === 0 && emptyEl) emptyEl.style.display = 'block';
  }

  /**
   * マイページ用：1件のカードを追加（削除ボタン付き）
   */
  appendOneMypageCard(product, container) {
    if (!container) return;
    const productId = product.id.replace('gid://shopify/Product/', '');
    const price = product.priceRange.minVariantPrice;
    const imageUrl = product.featuredImage ? product.featuredImage.url : '';
    const productUrl = '/products/' + product.handle;
    const priceHtml = this.formatPrice(price ? price.amount : 0);

    const li = document.createElement('li');
    li.className = 'grid__item small--one-half medium-up--one-quarter';
    li.innerHTML = `
      <div class="grid-view-item product-card">
        <a class="grid-view-item__link grid-view-item__image-container full-width-link" href="${productUrl}">
          <span class="visually-hidden">${this.escapeHtml(product.title)}</span>
        </a>
        <div class="product-card__image-with-placeholder-wrapper">
          <div class="grid-view-item__image-wrapper product-card__image-wrapper js">
            <div style="padding-top:100.0%;">
              <img class="grid-view-item__image" alt="" src="${imageUrl || ''}" style="max-width:250px;max-height:250px;">
            </div>
          </div>
          <div class="product-card__remove">
            <button type="button" class="favorites-remove-btn favorites-btn favorites-btn--small favorites-btn--remove" data-product-id="${productId}" data-product-handle="${product.handle}" data-product-title="${this.escapeHtml(product.title)}" data-product-price="${price && price.amount != null ? price.amount : ''}" aria-label="${this.getStrings().removeFromFavorites}">
              <i class="favorites-btn__icon fa-solid fa-xmark" aria-hidden="true"></i>
            </button>
          </div>
        </div>
        <div class="h3 grid-view-item__title product-card__title" aria-hidden="true">${this.escapeHtml(product.title)}</div>
        <dl class="price price--listing">
          <div class="price__regular">
            <dt><span class="visually-hidden visually-hidden--inline">${this.getStrings().regularPrice}</span></dt>
            <dd><span class="price-item price-item--regular">${priceHtml}</span></dd>
          </div>
        </dl>
      </div>
    `;

    const removeBtn = li.querySelector('.favorites-remove-btn');
    if (removeBtn) {
      removeBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        const itemInfo = { productName: removeBtn.getAttribute('data-product-title') || '', productPrice: removeBtn.getAttribute('data-product-price') || '' };
        await this.removeFavorite(productId, itemInfo);
        li.remove();
        const gridEl = document.getElementById('mypage-favorites-grid');
        const emptyEl = document.getElementById('mypage-favorites-empty');
        const viewAllEl = document.getElementById('mypage-favorites-view-all');
        if (gridEl && gridEl.children.length === 0) {
          gridEl.style.display = 'none';
          if (emptyEl) emptyEl.style.display = 'block';
          if (viewAllEl) viewAllEl.style.display = 'none';
        }
      });
    }
    container.appendChild(li);
  }

  /**
   * マイページトップ用のお気に入りグリッドを一括レンダリング
   */
  renderMypageFavoritesGrid(products, container) {
    if (!container) return;
    container.innerHTML = '';
    products.forEach(p => this.appendOneMypageCard(p, container));
  }

  /**
   * 翻訳文字列を取得（theme.favoritesStrings 優先 → theme.strings → 日本語フォールバック）
   * theme.favoritesStrings は theme.liquid で現在ロケールの | t により出力されるため、英語時は英語が入る
   */
  getStrings() {
    const fav = (typeof theme !== 'undefined' && theme.favoritesStrings) ? theme.favoritesStrings : {};
    const s = (typeof theme !== 'undefined' && theme.strings) ? theme.strings : {};
    const get = (key) => fav[key] || s[key];
    return {
      regularPrice: get('regularPrice') || '通常価格',
      taxIncluded: get('taxIncluded') || '税込',
      removeFromFavorites: get('removeFromFavorites') || 'お気に入りから削除',
      addToFavorites: get('addToFavorites') || 'お気に入りに追加',
      favorited: get('favorited') || 'お気に入り',
      addedToFavorites: get('addedToFavorites') || 'お気に入りに追加しました',
      removedFromFavorites: get('removedFromFavorites') || 'お気に入りから削除しました',
      alreadyInFavorites: get('alreadyInFavorites') || '既にお気に入りに登録されています',
      favoritesError: get('favoritesError') || 'エラーが発生しました。後でもう一度お試しください。',
      loginToUse: get('loginToUse') || 'お気に入り機能を使うにはログインが必要です。',
      loginRegisterLink: get('loginRegisterLink') || '会員登録/ログインはこちら',
      addingToFavorites: get('addingToFavorites') || '追加中…',
      removingFromFavorites: get('removingFromFavorites') || '解除中…'
    };
  }

  /**
   * ログアウト時：お気に入りクリックで「ログインが必要」メッセージ＋会員登録/ログインリンクを表示
   */
  showLoginRequiredNotification() {
    const existing = document.querySelector('.favorites-notification');
    if (existing) existing.remove();

    const strings = this.getStrings();
    const loginUrl = (typeof theme !== 'undefined' && theme.accountLoginUrl) ? theme.accountLoginUrl : '/account/login';
    const linkText = strings.loginRegisterLink || '会員登録/ログインはこちら';
    const message = strings.loginToUse || 'お気に入り機能を使うにはログインが必要です。';

    const notification = document.createElement('div');
    notification.className = 'favorites-notification favorites-notification--info';
    notification.innerHTML = message + ' <a href="' + this.escapeHtml(loginUrl) + '" class="favorites-notification__link">' + this.escapeHtml(linkText) + '</a>';
    document.body.appendChild(notification);

    setTimeout(() => notification.classList.add('is-visible'), 10);
    setTimeout(() => {
      notification.classList.remove('is-visible');
      setTimeout(() => notification.remove(), 300);
    }, 5000);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * 価格フォーマット（多言語・最近見た商品と同じ表示）
   * Shopify の /products/{handle}.js では JPY が100分の1単位で返る（330000 = ¥3,300）
   */
  formatPrice(amount) {
    const strings = this.getStrings();
    const taxLabel = strings.taxIncluded;
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      const formatted = Shopify.formatMoney(amount);
      return formatted + '<span class="price-item-tax">（' + taxLabel + '）</span>';
    }
    const n = parseInt(parseFloat(amount), 10);
    const displayAmount = isNaN(n) ? 0 : Math.round(n / 100);
    return '¥' + displayAmount.toLocaleString() + '<span class="price-item-tax">（' + taxLabel + '）</span>';
  }

  /**
   * お気に入りページ用：1件のカードを追加（削除ボタン付き）
   */
  appendOneFavoriteCard(product, container) {
    if (!container) return;
    const productId = product.id.replace('gid://shopify/Product/', '');
    const price = product.priceRange.minVariantPrice;
    const imageUrl = product.featuredImage ? product.featuredImage.url : '';
    const productUrl = '/products/' + product.handle;
    const priceHtml = this.formatPrice(price ? price.amount : 0);

    const li = document.createElement('li');
    li.className = 'grid__item small--one-half medium-up--one-quarter';
    li.innerHTML = `
      <div class="grid-view-item product-card">
        <a class="grid-view-item__link grid-view-item__image-container full-width-link" href="${productUrl}">
          <span class="visually-hidden">${this.escapeHtml(product.title)}</span>
        </a>
        <div class="product-card__image-with-placeholder-wrapper">
          <div class="grid-view-item__image-wrapper product-card__image-wrapper js">
            <div style="padding-top:100.0%;">
              <img class="grid-view-item__image" alt="" src="${imageUrl || ''}" style="max-width:250px;max-height:250px;">
            </div>
          </div>
          <div class="product-card__remove">
            <button type="button" class="favorites-remove-btn favorites-btn favorites-btn--small favorites-btn--remove" data-product-id="${productId}" data-product-handle="${product.handle}" data-product-title="${this.escapeHtml(product.title)}" data-product-price="${price && price.amount != null ? price.amount : ''}" aria-label="${this.getStrings().removeFromFavorites}">
              <i class="favorites-btn__icon fa-solid fa-xmark" aria-hidden="true"></i>
            </button>
          </div>
        </div>
        <div class="h3 grid-view-item__title product-card__title" aria-hidden="true">${this.escapeHtml(product.title)}</div>
        <dl class="price price--listing">
          <div class="price__regular">
            <dt><span class="visually-hidden visually-hidden--inline">${this.getStrings().regularPrice}</span></dt>
            <dd><span class="price-item price-item--regular">${priceHtml}</span></dd>
          </div>
        </dl>
      </div>
    `;

    const removeBtn = li.querySelector('.favorites-remove-btn');
    if (removeBtn) {
      removeBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        const itemInfo = { productName: removeBtn.getAttribute('data-product-title') || '', productPrice: removeBtn.getAttribute('data-product-price') || '' };
        await this.removeFavorite(productId, itemInfo);
        li.remove();
        if (container.children.length === 0) {
          const sectionEl = document.getElementById('favorites-section');
          const emptyEl = document.getElementById('favorites-empty');
          if (sectionEl) sectionEl.style.display = 'none';
          if (emptyEl) emptyEl.style.display = 'block';
        }
      });
    }
    container.appendChild(li);
  }

  /**
   * お気に入りグリッドを一括レンダリング
   */
  renderFavoritesGrid(products, container) {
    if (!container) return;
    container.innerHTML = '';
    products.forEach(p => this.appendOneFavoriteCard(p, container));
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// グローバルに公開（Liquidから使用できるように）
window.FavoritesManager = FavoritesManager;

// Boost PFS（Boost AI Search & Filter）で商品一覧が再描画されたらお気に入りボタンの状態を更新
function setupBoostPFSFavoritesObserver() {
  var container = document.querySelector('.boost-pfs-filter-products');
  if (!container || !window.favoritesManager) return;
  var timeoutId = null;
  var observer = new MutationObserver(function() {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(function() {
      if (typeof window.favoritesManager.updateAllButtonStates === 'function') {
        window.favoritesManager.updateAllButtonStates();
      }
      timeoutId = null;
    }, 100);
  });
  observer.observe(container, { childList: true, subtree: true });
}

// ページロード時に初期化（ログアウト時もボタン表示・クリックで「ログインすると使えるようになります」を表示）
document.addEventListener('DOMContentLoaded', function() {
  window.favoritesManager = new FavoritesManager();
  if (typeof window.customer !== 'undefined' && window.customer !== null) {
    // マイページトップ: ここで呼ぶ（account.liquid の script より先に実行される可能性があるため）
    if (document.getElementById('mypage-favorites-grid')) {
      window.favoritesManager.loadMypageFavorites();
    }
    // お気に入り専用ページ
    if (document.getElementById('favorites-grid') && document.getElementById('favorites-loading')) {
      window.favoritesManager.loadFavoritesPage();
    }
  }
  // Boost PFS 商品一覧: フィルター/ソート/読み込み後にボタン状態を更新
  if (document.querySelector('.boost-pfs-filter-products')) {
    setupBoostPFSFavoritesObserver();
    if (typeof window.favoritesManager.updateAllButtonStates === 'function') {
      window.favoritesManager.updateAllButtonStates();
    }
  }
});
